import { supabase } from "@/integrations/supabase/client";
import { getTmdbLanguage } from "@/i18n/config";

export interface TMDBProvider {
  provider_id: number;
  provider_name: string;
  logo_path: string;
}

export interface TMDBItem {
  id: number;
  title: string;
  synopsis: string;
  poster: string;
  year: number;
  rating: number;
  genres: string[];
  genre_ids: number[];
  media_type: string;
  runtime?: number;
  first_air_date?: string | null;
  last_air_date?: string | null;
  certification?: string;
}

export async function fetchProviders(region: string): Promise<TMDBProvider[]> {
  const { data, error } = await supabase.functions.invoke('tmdb-providers', {
    body: null,
    headers: {},
  });

  // supabase.functions.invoke doesn't support query params, so we use the URL directly
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  const language = getTmdbLanguage();
  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/tmdb-providers?region=${region}&language=${language}`,
    {
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch providers');
  }

  const result = await response.json();
  return (result.providers || []).map((p: any) => ({
    provider_id: p.provider_id,
    provider_name: p.provider_name,
    logo_path: p.logo_path ? `https://image.tmdb.org/t/p/w92${p.logo_path}` : '',
  }));
}

export async function fetchPopular(
  type: 'movie' | 'tv' = 'movie',
  page = 1,
  region = 'GB',
  query = '',
  ageFilter?: { is15Plus: boolean; is18Plus: boolean }
): Promise<{ results: TMDBItem[]; total_pages: number }> {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const language = getTmdbLanguage();
  const params = new URLSearchParams({ type, page: String(page), region, language });
  if (query) params.set('query', query);
  if (ageFilter) {
    params.set('is_15_plus', String(ageFilter.is15Plus));
    params.set('is_18_plus', String(ageFilter.is18Plus));
  }

  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/tmdb-popular?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
    }
  );

  if (!response.ok) throw new Error('Failed to fetch popular');
  return response.json();
}

export interface RecommendationParams {
  favoriteIds: number[];
  mediaType: 'movie' | 'tv';
  genreIds?: number[];
  providerIds?: number[];
  maxRuntime?: number;
  region?: string;
  dateRange?: { gte?: string; lte?: string };
  validYears?: number[];
  is15Plus?: boolean;
  is18Plus?: boolean;
  excludeIds?: number[];
  discoverPageStart?: number;
  userId?: string;
}

/**
 * Build a stable cache key for recommendation params.
 * Excludes exclude_ids and discover_page_start since those change per pagination.
 */
function buildRecsCacheKey(params: RecommendationParams): string {
  const parts = [
    params.mediaType,
    (params.genreIds || []).sort().join(','),
    (params.providerIds || []).sort().join(','),
    params.maxRuntime || 180,
    params.region || 'GB',
    params.dateRange?.gte || '',
    params.dateRange?.lte || '',
    (params.validYears || []).sort().join(','),
    params.is15Plus ?? true,
    params.is18Plus ?? true,
    params.discoverPageStart || 1,
  ].join('|');
  return `recs_client_${parts}`;
}

export async function fetchRecommendations(
  params: RecommendationParams
): Promise<{ results: TMDBItem[] }> {
  // Check sessionStorage cache first
  const cacheKey = buildRecsCacheKey(params);
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Cache valid for 30 minutes
      if (parsed._ts && Date.now() - parsed._ts < 30 * 60 * 1000) {
        return { results: parsed.results };
      }
      sessionStorage.removeItem(cacheKey);
    }
  } catch {}

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/tmdb-recommendations`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        favorite_ids: params.favoriteIds,
        media_type: params.mediaType,
        genre_ids: params.genreIds || [],
        provider_ids: params.providerIds || [],
        max_runtime: params.maxRuntime || 180,
        region: params.region || 'GB',
        language: getTmdbLanguage(),
        date_gte: params.dateRange?.gte,
        date_lte: params.dateRange?.lte,
        valid_years: params.validYears || [],
        is_15_plus: params.is15Plus ?? true,
        is_18_plus: params.is18Plus ?? true,
        exclude_ids: params.excludeIds || [],
        discover_page_start: params.discoverPageStart || 1,
        user_id: params.userId || null,
      }),
    }
  );

  if (!response.ok) throw new Error('Failed to fetch recommendations');
  const data = await response.json();

  // Store in sessionStorage (fire-and-forget, ignore quota errors)
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify({ ...data, _ts: Date.now() }));
  } catch {}

  return data;
}

/**
 * Fetch full TMDB details for a batch of IDs (synopsis, runtime, rating, genres, providers, etc).
 * Used to enrich bookmark / currently-watching items missing metadata.
 */
export async function fetchTmdbDetails(
  ids: number[],
  mediaType: 'movie' | 'tv' = 'movie',
  region = 'GB'
): Promise<Record<number, { synopsis: string; runtime: number; rating: number; genres: string[]; certification: string; providers: WatchProvider[]; watch_link: string; first_air_date?: string | null; last_air_date?: string | null; year: number; poster: string }>> {
  if (ids.length === 0) return {};

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const language = getTmdbLanguage();

  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/tmdb-details`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids, media_type: mediaType, region, language }),
    }
  );

  if (!response.ok) return {};
  const data = await response.json();
  return data.results || {};
}

export interface WatchProvider {
  provider_id: number;
  provider_name: string;
  logo_path: string;
}

export interface WatchProviderResult {
  providers: WatchProvider[];
  link: string | null;
}

export async function fetchWatchProviders(
  id: number,
  mediaType: 'movie' | 'tv' = 'movie',
  region = 'GB'
): Promise<WatchProviderResult> {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const params = new URLSearchParams({ id: String(id), media_type: mediaType, region, language: getTmdbLanguage() });

  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/tmdb-watch-providers?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
    }
  );

  if (!response.ok) return { providers: [], link: null };
  const data = await response.json();
  return { providers: data.providers || [], link: data.link || null };
}
